/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useContext, useEffect, useState } from 'react';

import type { LearnerProfile_ExternalLinks as ExternalLinks } from '__generated__/graphql-types-do-not-use';

import requestCountry from 'js/lib/requestCountry';
import { useRetracked } from 'js/lib/retracked';

import { Button, Tag, Typography2 } from '@coursera/cds-core';
import { AddIcon } from '@coursera/cds-icons';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import { ProfileContext } from 'bundles/account-profile/components/recruiting-profile-viewer/ProfileContextProvider';
import ProfileWebsiteInput from 'bundles/account-profile/components/recruiting-profile-viewer/components/additional-info/ExternalLinksInput';
import { EVENTS } from 'bundles/account-profile/components/recruiting-profile-viewer/eventing/events';
import type { ExternalLinksOption } from 'bundles/account-profile/components/recruiting-profile-viewer/utils/additionalInfoUtils';
import {
  formatWebsiteData,
  getWebsitesData,
} from 'bundles/account-profile/components/recruiting-profile-viewer/utils/additionalInfoUtils';
import TrackedDiv from 'bundles/page/components/TrackedDiv';

import _t from 'i18n!nls/account-profile';

export type Props = {
  onUpdate: (externalLinks: ExternalLinks) => void;
  values?: ExternalLinks;
  validationStatus?: 'success' | 'error';
  validationLabel?: string;
};

const styles = {
  label: () => css`
    margin-bottom: var(--cds-spacing-50);
    border: 0;
  `,
  addButton: () => css`
    margin-top: var(--cds-spacing-100);
  `,
  validationLabel: () => css`
    margin: var(--cds-spacing-150) 0;
  `,
};

const ExternalLinkInputs = React.forwardRef<HTMLFieldSetElement, Props>(
  ({ onUpdate, values, validationStatus, validationLabel }, ref) => {
    const { sharedEventingV3Data, restrictHiringSolutions } = useContext(ProfileContext);
    const retrackedTrack = useRetracked();
    const track = useTracker();
    const visibilityTrackingRef: React.MutableRefObject<HTMLButtonElement | null> = useVisibilityTracker(
      'view_button',
      {
        button: {
          name: EVENTS.addWebsiteLink,
        },
        ...sharedEventingV3Data,
      }
    );

    const allOptions = getWebsitesData({}, requestCountry.get());
    const allOptionsWithValues = getWebsitesData(values || {}, requestCountry.get());
    const [freeOptions, setFreeOptions] = useState<Array<ExternalLinksOption>>(
      allOptionsWithValues.filter(({ value }) => !value)
    );
    const [filledOptions, setFilledOptions] = useState<Array<ExternalLinksOption>>(
      allOptionsWithValues.filter(({ value }) => value)
    );

    const addChildInput = () => {
      if (filledOptions.length >= allOptions.length) {
        return;
      }
      // @ts-expect-error TS2769 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      setFilledOptions(filledOptions.concat(freeOptions[0]));
      setFreeOptions(freeOptions.slice(1));
    };

    const addChildIfEmpty = () => {
      if (!filledOptions.length) {
        addChildInput();
      }
    };
    const handleChange = (updatedOptions: ExternalLinksOption[]) => {
      onUpdate(formatWebsiteData(updatedOptions));
    };

    const handleSelect = (currentIndex: number, newIndex: number, oldIndexValue?: string) => {
      const updatedFilledOptions = filledOptions.map((option) =>
        // allOptions are the empty states of the external link. If there is an existing link value we need to include it, otherwise it will be overwritten
        // with an empty string
        option.index !== currentIndex ? option : { ...allOptions[newIndex], value: oldIndexValue }
      );
      const updatedFreeOptions = [
        ...freeOptions.filter(({ index }) => index !== newIndex),
        allOptions[currentIndex],
        // @ts-expect-error TS18048 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      ].sort((a, b) => (a.index > b.index ? -1 : 1));
      // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      setFreeOptions(updatedFreeOptions);
      // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      setFilledOptions(updatedFilledOptions);
      // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      handleChange(updatedFilledOptions);
    };

    const handleRemove = (removedIndex: number) => {
      const updatedFilledOptions = filledOptions.filter(({ index }) => index !== removedIndex);
      const updatedFreeOptions = [...freeOptions, allOptions[removedIndex]].sort((a, b) =>
        // @ts-expect-error TS18048 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
        a.index > b.index ? -1 : 1
      );
      // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      setFreeOptions(updatedFreeOptions);
      setFilledOptions(updatedFilledOptions);
      addChildIfEmpty();
      handleChange(updatedFilledOptions);
    };

    const handleValueChange = (value: string, updatedIndex: number) => {
      const updatedFilledOptions = filledOptions.map((option) =>
        option.index !== updatedIndex ? option : { ...option, value }
      );
      setFilledOptions(updatedFilledOptions);
      handleChange(updatedFilledOptions);
    };

    const handleAddLinkBtnClick = () => {
      retrackedTrack({
        trackingData: {},
        trackingName: EVENTS.addWebsiteLink,
        action: 'click',
      });

      track('click_button', {
        button: {
          name: EVENTS.addWebsiteLink,
        },
        ...sharedEventingV3Data,
      });

      addChildInput();
    };

    useEffect(() => {
      // Ensure there's at least one input if there's no externalLinks in the data
      addChildIfEmpty();
      // We only want to call this function once on the initial render
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getAdditionalLinksText = () => {
      return restrictHiringSolutions
        ? _t('Already have a LinkedIn profile, GitHub account, or personal portfolio? Add up to 5 links.')
        : _t(
            'Already have a LinkedIn profile, GitHub account, or personal portfolio? Add up to 5 links to make it easier for employers to view your work.'
          );
    };

    return (
      <fieldset ref={ref}>
        <Typography2 css={styles.label} component="legend" variant="subtitleMedium">
          {_t('Additional links')}
        </Typography2>
        <Typography2 component="p" variant="bodySecondary">
          {getAdditionalLinksText()}
        </Typography2>
        {filledOptions.map((option) => (
          <ProfileWebsiteInput
            key={`profile-website-input-${option.id}`}
            options={freeOptions}
            selectedOption={option}
            onSelect={handleSelect}
            onRemove={handleRemove}
            hideRemove={filledOptions.length <= 1}
            onValueChange={handleValueChange}
          />
        ))}
        {freeOptions.length > 0 && (
          <TrackedDiv
            trackingName={EVENTS.addWebsiteLink}
            withVisibilityTracking
            requireFullyVisible={false}
            trackClicks={false}
          >
            <Button
              ref={visibilityTrackingRef}
              icon={<AddIcon />}
              iconPosition="before"
              variant="ghost"
              size="small"
              onClick={handleAddLinkBtnClick}
              css={styles.addButton}
            >
              {_t('Add link')}
            </Button>
          </TrackedDiv>
        )}
        {validationStatus === 'error' && validationLabel && (
          <div>
            <Tag css={styles.validationLabel} variant="status" color="error" priority="secondary">
              {validationLabel}
            </Tag>
          </div>
        )}
      </fieldset>
    );
  }
);

export default ExternalLinkInputs;
